/**
 * The activity feed's wording (projects/activity-feed D4). A row is one
 * task's events on one day; the server picks its headline — the most
 * significant event of the whole day — and the rest become a short tally.
 */
import { statusLabel } from "./activityText";
import { displayName } from "./userDisplay";

/**
 * The headline split around the task's title, so the title can be a link:
 * `${actor} ${verb} <title>${suffix}` — "scout moved Fix the gate to
 * In Progress".
 */
export function headlineParts(event) {
  const p = event?.payload || {};
  const actor = event?.actor ? displayName(event.actor) : "Someone";
  const parts = (verb, suffix = "") => ({ actor, verb, suffix });
  switch (event?.verb) {
    case "status_changed":
      if (p.to === "DONE") return parts("finished");
      if (p.to === "CANCELED") return parts("canceled");
      return parts("moved", ` to ${statusLabel(p.to)}`);
    case "created":
      return parts("created");
    case "archived":
      return parts("archived");
    case "unarchived":
      return parts("restored");
    case "assigned":
      return p.to
        ? parts("assigned", ` to ${displayName(p.to)}`)
        : parts("unassigned");
    case "claimed":
      return parts("claimed");
    case "comment":
      return parts("commented on");
    case "attached":
      return parts("attached a file to");
    case "detached":
      return parts("removed a file from");
    case "drawing_linked":
      return parts("linked a drawing to");
    case "drawing_unlinked":
      return parts("unlinked a drawing from");
    case "released":
      return parts("released");
    default:
      return parts("updated");
  }
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * What else happened that day, beside the headline: "3 comments · 1 file
 * added · 2 more updates". `counts` covers every event in the row.
 */
export function tally(counts, headline) {
  const left = { ...counts };
  if (headline && left[headline.verb]) left[headline.verb] -= 1;
  const comments = left.comment || 0;
  const files = left.attached || 0;
  const rest =
    Object.values(left).reduce((sum, n) => sum + n, 0) - comments - files;
  const bits = [];
  if (comments) bits.push(plural(comments, "comment", "comments"));
  if (files) bits.push(`${plural(files, "file", "files")} added`);
  if (rest) bits.push(plural(rest, "more update", "more updates"));
  return bits.join(" · ");
}

/** The reader's calendar day "YYYY-MM-DD" — the API's `day` format. */
export const localDay = (date = new Date()) => date.toLocaleDateString("en-CA");

/** "Today", "Yesterday", else "Wednesday, Sep 30". */
export function dayLabel(day, now = new Date()) {
  if (day === localDay(now)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (day === localDay(yesterday)) return "Yesterday";
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}
